/**
 * One pull request review in the sheet: the record of what Proctor read, what
 * it would say, what it threw away and why, and the one verb the state asks for.
 *
 *   header   "<repo> #<n>", Proctor's medallion and "Review", the title, the
 *            state pill, author, head, the Team (or "no Team"), when it opened
 *   body     The review (verdict, the state sentence, the pass facts);
 *            Findings (Keep, Edit, Remove per finding while held);
 *            Discarded (open whenever Proctor dropped anything);
 *            Checks that ran (open); Fix rounds; More details; History
 *   footer   held       Re-run review / Confirm review
 *            reviewing  Re-run review / Watch live
 *            followup   Archive / Re-run review
 *            posted     View pull request / Next review
 *            closed     View pull request / Review again
 *
 * Every verb is an existing route (`reviewsApi.ts`, and the gate resolve for
 * Confirm). The pull request's CI state is not on any review read, so the
 * sheet does not print a CI chip rather than guess one.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { CodeReviewPrReviewsResponse, ReviewFindingDecision } from "@yagni/shared";
import {
  Fold,
  MenuItem,
  MenuSeparator,
  Pill,
  RecordHead,
  SheetFoot,
  Chip,
  type SheetVerb,
} from "../queue/index.js";
import { useSheet } from "../shell/useSheet.js";
import { useOpenWorkItemGate, useResolveWorkItemGate } from "../tracker/useWorkItemGate.js";
import { useReviewFindings } from "../work/ReviewFindings.js";
import { useWorkerLookup } from "../work/useWorkerLookup.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { CODE_REVIEW_SLUG } from "../workers/crafts.js";
import { REVIEW_PASS_CHIP, durationLabel, runHistoryLine } from "../workers/copy.js";
import {
  viewPrLabel,
  REVIEW_CONFIRM_KEPT_DRAFT,
  REVIEW_CONFIRM_NOT_POSTED_FAILED,
  REVIEW_CONFIRM_NOT_POSTED_STALE,
  REVIEW_CONFIRM_NOTHING_KEPT,
} from "../work/workCopy.js";
import { reviewFailureSentence, reviewRetryLine } from "../work/review/reviewCopy.js";
import { shortAge } from "../ticket/queueGroups.js";
import { toast } from "../ui/toast.js";
import { captureUiError } from "../sentry.js";
import type { ReviewsApi } from "./reviewsApi.js";
import {
  REVIEW_STATE_PILL,
  checksOf,
  deliveredFindingsOf,
  discardedOf,
  repoName,
  reviewStateOf,
  roundOf,
  severityColor,
  verdictWord,
  type ReviewState,
} from "./reviewsModel.js";

const POLL_MS = 10_000;

export interface ReviewSheetProps {
  api: ReviewsApi;
  prStateId: string;
  /** The Team whose listing carried the pull request: where Review again asks. */
  deskId: string | null;
  deskName: string | null;
  /** The repository is still on the catch-all Reviews team. */
  unowned: boolean;
  /** A write landed: the list re-reads. */
  onChanged: () => void;
  /** The next held review, or null when none waits. */
  onNextReview?: (() => void) | null;
  /** Opens the move sheet for this repository. */
  onMoveRepo?: (() => void) | null;
  onClose: () => void;
  /** Where Copy link points. */
  shareUrl?: string;
  /** Who reviews a repository, for a pull request the list did not carry (a deep link). */
  deskFor?: (
    repoFullName: string,
  ) => { deskId: string | null; deskName: string | null; unowned: boolean } | null;
  now?: number;
}

const DECISION_LABEL: Record<ReviewFindingDecision, string> = {
  keep: "kept as drafted",
  edited: "Edit",
  removed: "Remove",
};

export function ReviewSheet({
  api,
  prStateId,
  deskId: listedDeskId,
  deskName: listedDeskName,
  unowned: listedUnowned,
  deskFor,
  onChanged,
  onNextReview,
  onMoveRepo,
  onClose,
  shareUrl,
  now = Date.now(),
}: ReviewSheetProps) {
  const sheet = useSheet();
  const workerOf = useWorkerLookup();
  const proctor = workerOf(CODE_REVIEW_SLUG);
  const [data, setData] = useState<CodeReviewPrReviewsResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [reload, setReload] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Whether a read has landed, so a failed re-read can keep the record on screen.
  const shown = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .prReviews(prStateId)
      .then((body) => {
        if (cancelled) return;
        shown.current = body != null;
        setData(body);
        setStatus(body ? "ready" : "missing");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, { area: "Reviews", operation: "read_pr_reviews", severity: "warning" });
        if (shown.current) {
          setNote("Could not refresh this review just now. What shows may be out of date.");
          return;
        }
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [api, prStateId, reload]);

  const live = data?.reviews.some((report) => report.status === "in_progress") ?? false;
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => setReload((n) => n + 1), POLL_MS);
    return () => clearInterval(timer);
  }, [live]);

  const pr = data?.pr ?? null;
  const owner = listedDeskId == null && pr && deskFor ? deskFor(pr.repoFullName) : null;
  const deskId = listedDeskId ?? owner?.deskId ?? null;
  const deskName = listedDeskId != null ? listedDeskName : (owner?.deskName ?? null);
  const unowned = listedDeskId != null ? listedUnowned : (owner?.unowned ?? false);
  const state: ReviewState | null = pr ? (reviewStateOf(pr, now) ?? fallbackState(pr)) : null;
  const held = state === "held";
  const workItemId = pr?.workItemId ?? null;
  const { gate, refetch: refetchGate } = useOpenWorkItemGate(held ? workItemId : null);
  const review = useReviewFindings(workItemId ?? "", held && gate?.kind === "review_result");
  const { resolveGate } = useResolveWorkItemGate();

  const reports = data?.reviews ?? [];
  const newest = reports[0] ?? null;
  const discarded = useMemo(() => discardedOf(newest), [newest]);
  const checks = useMemo(() => checksOf(newest), [newest]);
  const posted = useMemo(() => deliveredFindingsOf(newest), [newest]);
  const followUps = reports.filter((report) => isFollowUp(report.stages));

  const changed = useCallback(() => {
    setReload((n) => n + 1);
    void refetchGate();
    onChanged();
  }, [onChanged, refetchGate]);

  const run = async (
    label: string,
    act: () => Promise<{ ok: boolean; message?: string }>,
    done: string,
  ) => {
    setBusy(true);
    setNote(null);
    try {
      const result = await act();
      if (!result.ok) {
        setNote(result.message || `${label} did not go through.`);
        return;
      }
      setNote(done);
      changed();
    } finally {
      setBusy(false);
    }
  };

  const rerun = () =>
    void run(
      "Re-run review",
      async () => {
        const result = await api.rerun(prStateId);
        return result.ok ? { ok: true } : { ok: false, message: result.message };
      },
      "Re-run started. Proctor composes a fresh review.",
    );
  const reviewAgain = () => {
    if (!deskId || !pr) return;
    void run(
      "Review again",
      async () => {
        const result = await api.requestReview(deskId, { input: pr.prUrl, repo: pr.repoFullName });
        return result.ok ? { ok: true } : { ok: false, message: result.message };
      },
      "A fresh review is queued. It opens as its own case.",
    );
  };
  const archive = () => {
    if (!workItemId) return;
    void run(
      "Archive",
      async () => {
        const result = await api.archiveWorkItem(workItemId);
        return result.ok ? { ok: true } : { ok: false, message: result.message };
      },
      "Archived. It is out of every list until someone brings it back.",
    );
  };
  const openPr = () => {
    if (pr) window.open(pr.prUrl, "_blank", "noopener,noreferrer");
  };
  const confirm = async () => {
    if (!gate || !workItemId) return;
    setBusy(true);
    setNote(null);
    try {
      const resolved = await resolveGate({
        workItemId,
        gateId: gate.id,
        action: "accept_result",
        ...(review.resolutions.length > 0 ? { findings: review.resolutions } : {}),
      });
      setNote(confirmNote(resolved?.reviewDelivery));
      window.dispatchEvent(new CustomEvent("tickets.invalidate"));
      changed();
    } catch (err) {
      setNote(err instanceof Error ? err.message : "Could not confirm the review.");
    } finally {
      setBusy(false);
    }
  };
  const copyLink = () => {
    if (!shareUrl) return;
    void navigator.clipboard?.writeText(shareUrl).then(
      () => toast.success("Copied the link."),
      () => toast.error("Could not copy."),
    );
  };

  const kick = (
    <span className="stagebtn" style={{ "--stage": "var(--stage-review)" } as CSSProperties}>
      {proctor && (
        <WorkerMark
          avatar={proctor.avatar}
          name={proctor.displayName}
          slug={proctor.slug}
          size="sm"
        />
      )}
      Review
    </span>
  );

  if (!pr || !state) {
    return (
      <>
        <RecordHead
          recordKey="REVIEW"
          kick={kick}
          title={status === "loading" ? "Reading the review" : "Review"}
          onClose={onClose}
          noExpand
        />
        <div className="sbody">
          <p className="body-p" role={status === "error" ? "alert" : undefined}>
            {status === "loading"
              ? "Reading what Proctor recorded."
              : status === "missing"
                ? "This pull request is not watched in this workspace any more."
                : "Could not read this review just now."}
          </p>
        </div>
      </>
    );
  }

  const pill = REVIEW_STATE_PILL[state];
  const verdict = verdictWord(newest?.verdict ?? null);
  const round = roundOf(reports);
  const head = newest?.headSha ? newest.headSha.slice(0, 7) : null;
  const duration = newest
    ? (durationLabel(newest.startedAt, newest.completedAt) ??
      (live ? `running ${shortAge(newest.startedAt, now)}` : null))
    : null;
  // While held, the draft is the gate's findings; the report's survivors have not posted.
  const findingsHeld = held;
  const findingsCount = held ? review.findings.length : posted.length;
  const settled = pr.githubStatus === "merged" || pr.githubStatus === "closed";

  const sentence = stateSentence(state, {
    verdict: newest?.verdict ?? null,
    round,
    comments: posted.length,
    failure: newest?.failure?.reason ?? null,
    retry: data?.retry ?? null,
    githubStatus: pr.githubStatus,
    githubApproval: pr.githubApproval ?? null,
  });

  const more = (
    <>
      <MenuItem onSelect={openPr}>{viewPrLabel(pr.prNumber)} on GitHub</MenuItem>
      {!settled && <MenuItem onSelect={rerun}>Re-run review</MenuItem>}
      {deskId && <MenuItem onSelect={reviewAgain}>Review again</MenuItem>}
      {unowned && onMoveRepo && (
        <MenuItem onSelect={onMoveRepo}>Move this repository onto a Team</MenuItem>
      )}
      {shareUrl && <MenuItem onSelect={copyLink}>Copy link</MenuItem>}
      {workItemId && (
        <>
          <MenuSeparator />
          <MenuItem onSelect={archive}>Archive</MenuItem>
        </>
      )}
    </>
  );

  const footer = footerFor(state, {
    rerun: { label: "Re-run review", onClick: rerun, disabled: busy },
    confirm: {
      label: "Confirm review",
      onClick: () => void confirm(),
      disabled: busy || !gate || gate.kind !== "review_result",
    },
    watch: sheet.wide ? undefined : { label: "Watch live", onClick: () => sheet.setWide(true) },
    archive: workItemId ? { label: "Archive", onClick: archive, disabled: busy } : undefined,
    openPr: { label: viewPrLabel(pr.prNumber), onClick: openPr },
    next: { label: "Next review", onClick: () => (onNextReview ? onNextReview() : onClose()) },
    again: deskId ? { label: "Review again", onClick: reviewAgain, disabled: busy } : undefined,
  });

  return (
    <>
      <RecordHead
        recordKey={`${repoName(pr.repoFullName)} #${pr.prNumber}`}
        kick={kick}
        more={more}
        title={pr.title ?? `Pull request #${pr.prNumber}`}
        onClose={onClose}
        meta={
          <>
            <Pill kind={pill.kind} dot={pill.kind !== "done"}>
              {pill.label}
            </Pill>
            {pr.author && <Chip>{pr.author}</Chip>}
            {head && <Chip>head {head}</Chip>}
            <Chip>{unowned ? "no Team" : (deskName ?? "a Team you cannot see")}</Chip>
            {reports.length > 0 && (
              <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
                first read {shortAge(reports[reports.length - 1]!.startedAt, now)} ago
              </span>
            )}
          </>
        }
      />
      <div className="sbody" data-testid="review-sheet">
        <div className="blk">
          <div className="bh">
            <span className="label">The review</span>
            {verdict ? (
              <span
                className={`pill ${newest?.verdict === "approve" ? "live" : "done"}`}
                title="their call, before the review level, any hold or approval floor"
              >
                {verdict}
              </span>
            ) : (
              <span className="fact">{live ? "in progress" : "no verdict yet"}</span>
            )}
          </div>
          <p className="body-p" style={{ color: "var(--text-primary)" }}>
            {sentence}
          </p>
          {newest && (
            <div className="fact" style={{ fontSize: 11.5 }}>
              {[
                isFollowUp(newest.stages)
                  ? `${REVIEW_PASS_CHIP.followUp}, round ${round}`
                  : REVIEW_PASS_CHIP.initial,
                duration,
                head ? `head ${head}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          {note && (
            <p className="body-p" role="status" data-testid="review-sheet-note">
              {note}
            </p>
          )}
        </div>

        {findingsCount > 0 && (
          <div className="blk" data-testid="review-findings-block">
            <div className="bh">
              <span className="label">Findings</span>
              <span className="fact">
                {findingsCount} ·{" "}
                {findingsHeld ? "nothing posts until you say so" : "posted as one review"}
              </span>
            </div>
            <div className="findings">
              {findingsHeld
                ? review.findings.map((finding) => {
                    const choice = review.decisions.get(finding.id);
                    const decision = choice?.decision ?? "keep";
                    return (
                      <div
                        key={finding.id}
                        className="finding"
                        style={decision === "removed" ? { opacity: 0.55 } : undefined}
                        data-testid={`held-finding-${finding.id}`}
                      >
                        <FindingHead
                          severity={finding.severity}
                          check={finding.check}
                          file={finding.file}
                          line={finding.line}
                        />
                        {decision === "edited" ? (
                          <textarea
                            className="codebox"
                            style={{
                              width: "100%",
                              marginTop: 6,
                              fontFamily: "var(--body)",
                              fontSize: 12.5,
                            }}
                            rows={3}
                            value={choice?.body ?? finding.body}
                            aria-label={`Rewrite the ${finding.check ?? "review"} finding`}
                            onChange={(event) => review.setBody(finding.id, event.target.value)}
                          />
                        ) : (
                          <p
                            style={
                              decision === "removed"
                                ? { textDecoration: "line-through" }
                                : undefined
                            }
                          >
                            {finding.body}
                          </p>
                        )}
                        <div className="facts">
                          {(["keep", "edited", "removed"] as const).map((option) => (
                            <button
                              key={option}
                              type="button"
                              className={option === decision ? "ref" : "ref quiet"}
                              aria-pressed={option === decision}
                              onClick={() => review.setDecision(finding.id, option)}
                            >
                              {DECISION_LABEL[option]}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })
                : posted.map((finding, index) => (
                    <div key={finding.id || index} className="finding">
                      <FindingHead
                        severity={finding.severity}
                        check={finding.check}
                        file={finding.file}
                        line={finding.line}
                      />
                      <p>{finding.body}</p>
                    </div>
                  ))}
            </div>
          </div>
        )}

        {discarded.length > 0 && (
          <Fold title="Discarded" count={discarded.length} defaultOpen>
            <p>
              What Proctor raised and then threw out before posting, with the reason it recorded.
            </p>
            <div className="findings" data-testid="review-discarded">
              {discarded.map((entry) => (
                <div key={entry.key} className="finding" style={{ opacity: 0.8 }}>
                  <FindingHead
                    severity={entry.finding.severity}
                    check={entry.finding.check ?? entry.stage}
                    file={entry.finding.file}
                    line={entry.finding.line}
                  />
                  <p style={{ textDecoration: "line-through" }}>{entry.finding.body}</p>
                  <p className="fact" style={{ fontSize: 11.5 }}>
                    {entry.why}
                  </p>
                </div>
              ))}
            </div>
          </Fold>
        )}

        {checks.length > 0 && (
          <Fold title="Checks that ran" count={checks.length} defaultOpen>
            <div className="checks" data-testid="review-checks">
              {checks.map((check) => (
                <div key={check.key}>
                  <span
                    className={check.tone === "quiet" ? undefined : check.tone}
                    style={{ fontFamily: "var(--mono)", fontSize: 11, minWidth: 120 }}
                  >
                    {check.note}
                  </span>
                  <span>{check.label}</span>
                </div>
              ))}
            </div>
          </Fold>
        )}

        {followUps.length > 0 && (
          <Fold title="Fix rounds" count={followUps.length}>
            <div className="checks">
              {followUps.map((report) => (
                <div key={report.id}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
                    {runHistoryLine(report)}
                  </span>
                  <span>{verdictWord(report.verdict) ?? "no verdict"}</span>
                </div>
              ))}
            </div>
          </Fold>
        )}

        <Fold title="More details">
          <dl className="receipt">
            <dt>Source</dt>
            <dd>
              <a href={pr.prUrl} target="_blank" rel="noreferrer">
                {pr.repoFullName}#{pr.prNumber}
              </a>
            </dd>
            <dt>Team</dt>
            <dd>
              {unowned
                ? "the Reviews team, tuned by nobody"
                : (deskName ?? "a Team you cannot see")}
            </dd>
            <dt>Receipts</dt>
            <dd>
              {reports.length} review {reports.length === 1 ? "pass" : "passes"}
              {followUps.length > 0 ? `, ${followUps.length} follow-up` : ""}
            </dd>
            {workItemId && deskId && (
              <>
                <dt>Runs</dt>
                <dd>
                  <a
                    href={`/desks/${encodeURIComponent(deskId)}?item=${encodeURIComponent(workItemId)}`}
                  >
                    Open the review case and its runs
                  </a>
                </dd>
              </>
            )}
          </dl>
        </Fold>

        {reports.length > 0 && (
          <Fold title="History" count={reports.length}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {reports.map((report) => (
                <li key={report.id} style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
                  {runHistoryLine(report)}
                  {verdictWord(report.verdict) ? ` · ${verdictWord(report.verdict)}` : ""}
                </li>
              ))}
            </ul>
          </Fold>
        )}
      </div>
      <SheetFoot why={footer.why} secondary={footer.secondary} primary={footer.primary} />
    </>
  );
}

function FindingHead({
  severity,
  check,
  file,
  line,
}: {
  severity: string | null;
  check: string | null;
  file: string | null;
  line: number | null;
}) {
  return (
    <div className="fh">
      {severity && (
        <span className="sev" style={{ "--c": severityColor(severity) } as CSSProperties}>
          {severity}
        </span>
      )}
      <b>{check ?? "review"}</b>
      {file && (
        <span className="fact" style={{ fontSize: 11, marginLeft: "auto" }}>
          {file}
          {line != null ? `:${line}` : ""}
        </span>
      )}
    </div>
  );
}

const NOT_POSTED_NOTE: Record<string, string> = {
  pr_closed: REVIEW_CONFIRM_NOT_POSTED_STALE,
  head_moved: REVIEW_CONFIRM_NOT_POSTED_STALE,
  nothing_kept: REVIEW_CONFIRM_NOTHING_KEPT,
};

/** What a confirm says, from the delivery the resolve reported: never "posted" over a post that did not land. */
export function confirmNote(
  delivery: { posted: boolean; reason?: string; heldInDraft?: string } | null | undefined,
): string {
  if (delivery && !delivery.posted)
    return NOT_POSTED_NOTE[delivery.reason ?? ""] ?? REVIEW_CONFIRM_NOT_POSTED_FAILED;
  if (delivery?.heldInDraft) return REVIEW_CONFIRM_KEPT_DRAFT;
  return "Posted to the pull request as one review.";
}

function isFollowUp(stages: Record<string, { output?: unknown }>): boolean {
  const output = stages["prepare"]?.output as { followUp?: unknown } | undefined;
  return output?.followUp === true;
}

/** A pull request the groups would not print (a draft, an older review) still opens with a state. */
function fallbackState(pr: CodeReviewPrReviewsResponse["pr"]): ReviewState {
  if (pr.githubStatus === "merged" || pr.githubStatus === "closed") return "closed";
  if (pr.proctorStatus === "reviewed") return "posted";
  return "reviewing";
}

/** What a posted review did on GitHub: who approved it, or that it only advised. */
function postedOutcome(
  verdict: "approve" | "comment_only" | null,
  githubApproval: CodeReviewPrReviewsResponse["pr"]["githubApproval"],
): string {
  if (githubApproval === "review_app") return "Approved on GitHub by YAGNI Review.";
  if (githubApproval === "app") return "Approved on GitHub.";
  if (verdict === "approve") return "Advised approval; a person still approves.";
  return "Comment only.";
}

export function stateSentence(
  state: ReviewState,
  facts: {
    verdict: "approve" | "comment_only" | null;
    round: number;
    comments: number;
    failure: string | null;
    retry: CodeReviewPrReviewsResponse["retry"] | null;
    githubStatus: string;
    /** Who approved the current head on GitHub for Proctor, when anyone did. */
    githubApproval?: CodeReviewPrReviewsResponse["pr"]["githubApproval"];
  },
): string {
  switch (state) {
    case "held":
      return facts.verdict
        ? `Proctor would ${facts.verdict === "approve" ? "approve" : "comment only"}. The whole draft waits here: what you keep posts as drafted, your edits teach it.`
        : "Proctor holds this draft for you. What you keep posts as drafted, your edits teach it.";
    case "reviewing":
      return `Proctor is on round ${facts.round}. The whole-diff pass reads the change first; the other checks follow.`;
    case "followup": {
      const why = facts.failure
        ? reviewFailureSentence(facts.failure)
        : "The last attempt did not finish.";
      const retry = reviewRetryLine(facts.retry ?? null);
      return `${why} Nothing from the failed attempt was posted.${retry ? ` ${retry}` : ""}`;
    }
    case "posted":
      return `Posted as one review with ${facts.comments} ${facts.comments === 1 ? "comment" : "comments"}. ${postedOutcome(facts.verdict, facts.githubApproval ?? null)}`;
    case "closed":
      return `This pull request is ${facts.githubStatus === "merged" ? "merged" : "closed"}, so its watch is over.`;
  }
}

interface FooterVerbs {
  rerun: SheetVerb;
  confirm: SheetVerb;
  watch?: SheetVerb;
  archive?: SheetVerb;
  openPr: SheetVerb;
  next: SheetVerb;
  again?: SheetVerb;
}

export function footerFor(
  state: ReviewState,
  verbs: FooterVerbs,
): { why: ReactNode; secondary?: SheetVerb; primary?: SheetVerb } {
  switch (state) {
    case "held":
      return {
        why: (
          <>
            Confirming posts what you kept and edited to the pull request <b>as one review</b>.
            Nothing is posted until you say so.
          </>
        ),
        secondary: verbs.rerun,
        primary: verbs.confirm,
      };
    case "reviewing":
      return {
        why: "Proctor posts when it is done.",
        secondary: verbs.rerun,
        primary: verbs.watch,
      };
    case "followup":
      return {
        why: (
          <>
            Re-running composes a fresh review and retries the post.{" "}
            <b>Nothing from the failed attempt was posted.</b>
          </>
        ),
        secondary: verbs.archive,
        primary: verbs.rerun,
      };
    case "posted":
      return {
        why: (
          <>
            Posted to GitHub as advice. <b>A person still approves and merges.</b>
          </>
        ),
        secondary: verbs.openPr,
        primary: verbs.next,
      };
    case "closed":
      return {
        why: verbs.again ? (
          <>
            Review again asks Proctor for one more read as it stands, <b>as its own case</b>.
          </>
        ) : (
          "This pull request is no longer open, and no Team you can see reviews its repository."
        ),
        secondary: verbs.openPr,
        primary: verbs.again,
      };
  }
}
